import { EventWebsiteState, useUpdateEventWebsiteStateMutation } from '@graphql/generated';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useCallback, useMemo } from 'react';

export const useWebsitePublishCapabilityFeature = (): Readonly<{
  eventExposure: Readonly<{
    value: boolean;
    isLoading: boolean;
  }>;
  eventPublishedState: Readonly<{
    value: boolean;
    update: (published: boolean) => Promise<unknown>;
    isLoading: boolean;
    isUpdating: boolean;
  }>;
}> => {
  const { eventInfo, loading } = useEventInfo();

  const [isWebsitePublishedStateEnabled, isLoadingWebsitePublishedState] = useIsFeatureEnabled({ eventFeatureFlagKey: 'useWebsitePublishedState' });
  const isEventEligible = !isLoadingWebsitePublishedState && isWebsitePublishedStateEnabled;

  const featureValue = useFeatureValue('websitePublishCapabilityEnabled', {
    skip: !isEventEligible,
  });

  const [mutate, { loading: isUpdatingEventWebsiteState }] = useUpdateEventWebsiteStateMutation();

  const updateEventWebsiteState = useCallback(
    async (published: boolean) => {
      if (!eventInfo?.eventId) {
        return;
      }

      return mutate({
        variables: {
          payload: {
            eventId: eventInfo.eventId,
            websiteState: published ? EventWebsiteState.published : EventWebsiteState.draft,
          },
        },
      });
    },
    [eventInfo, mutate],
  );

  const eventExposure = useMemo(
    () => ({
      value: featureValue.value === 'on',
      isLoading: featureValue.loading,
    }),
    [featureValue.loading, featureValue.value],
  );

  const eventPublishedState = useMemo(
    () => ({
      value: eventInfo?.websiteState === EventWebsiteState.published,
      update: updateEventWebsiteState,
      isLoading: loading,
      isUpdating: isUpdatingEventWebsiteState,
    }),
    [eventInfo?.websiteState, loading, updateEventWebsiteState, isUpdatingEventWebsiteState],
  );

  return {
    eventExposure,
    eventPublishedState,
  };
};
